import type { DateFormat, TimeFormat } from '@treneo/shared/constants';
import {
  convertDateFormatToDayjs,
  convertTimeFormatToDayjs,
  createDayjsInstance,
  parseDateTimeString
} from '@treneo/shared/utils';

import type { CourseDateEntry } from '../data/types';

export type CourseDateErrorWithSession = {
  messageKey: string;
  sessionNumber: number;
};

export type CourseDateError = string | CourseDateErrorWithSession;

export interface CourseDateValidationResult {
  isValid: boolean;
  error?: CourseDateErrorWithSession;
}

export interface CourseDateOrderingValidationResult {
  isValid: boolean;
  error?: string;
}

export const validateCourseDateEntry = (
  courseDate: CourseDateEntry,
  index: number,
  dateFormat: DateFormat,
  timeFormat: TimeFormat
): CourseDateValidationResult => {
  const sessionNumber = index + 1;

  if (
    !courseDate.startDate ||
    !courseDate.endDate ||
    !courseDate.startTime ||
    !courseDate.endTime
  ) {
    return {
      isValid: false,
      error: {
        messageKey: 'courseForm.validation.sessionStartEndRequired',
        sessionNumber
      }
    };
  }

  const dayjsDateFormat = convertDateFormatToDayjs(dateFormat);
  const dayjsTimeFormat = convertTimeFormatToDayjs(timeFormat);

  const start = parseDateTimeString(
    courseDate.startDate,
    courseDate.startTime,
    dayjsDateFormat,
    dayjsTimeFormat
  );
  const end = parseDateTimeString(
    courseDate.endDate,
    courseDate.endTime,
    dayjsDateFormat,
    dayjsTimeFormat
  );

  if (!start || !end) {
    return {
      isValid: false,
      error: {
        messageKey: 'courseForm.validation.sessionInvalidStartDateTime',
        sessionNumber
      }
    };
  }

  if (!start.isValid()) {
    return {
      isValid: false,
      error: {
        messageKey: 'courseForm.validation.sessionInvalidStartDateTime',
        sessionNumber
      }
    };
  }

  if (!end.isValid()) {
    return {
      isValid: false,
      error: {
        messageKey: 'courseForm.validation.sessionInvalidEndDateTime',
        sessionNumber
      }
    };
  }

  if (start.isSame(end)) {
    return {
      isValid: false,
      error: {
        messageKey: 'courseForm.validation.sessionSameStartEnd',
        sessionNumber
      }
    };
  }

  if (start.isAfter(end)) {
    return {
      isValid: false,
      error: {
        messageKey: 'courseForm.validation.sessionStartAfterEnd',
        sessionNumber
      }
    };
  }

  const currentTime = createDayjsInstance();
  const minimumDate = currentTime.subtract(1, 'year');
  const maximumDate = currentTime.add(5, 'year');

  if (start.isBefore(minimumDate)) {
    return {
      isValid: false,
      error: {
        messageKey: 'courseForm.validation.sessionPastLimit',
        sessionNumber
      }
    };
  }

  if (start.isAfter(maximumDate)) {
    return {
      isValid: false,
      error: {
        messageKey: 'courseForm.validation.sessionFutureLimit',
        sessionNumber
      }
    };
  }

  return { isValid: true };
};

export const validateCourseDatesOrdering = (
  courseDates: CourseDateEntry[],
  courseTimezone: string,
  dateFormat: DateFormat,
  timeFormat: TimeFormat
): CourseDateOrderingValidationResult => {
  if (courseDates.length <= 1) {
    return { isValid: true };
  }

  const dayjsDateFormat = convertDateFormatToDayjs(dateFormat);
  const dayjsTimeFormat = convertTimeFormatToDayjs(timeFormat);

  for (let i = 0; i < courseDates.length - 1; i++) {
    const currentStartParsed = parseDateTimeString(
      courseDates[i].startDate,
      courseDates[i].startTime,
      dayjsDateFormat,
      dayjsTimeFormat
    );
    const nextStartParsed = parseDateTimeString(
      courseDates[i + 1].startDate,
      courseDates[i + 1].startTime,
      dayjsDateFormat,
      dayjsTimeFormat
    );

    if (!currentStartParsed || !nextStartParsed) {
      continue;
    }

    const currentStart = currentStartParsed.tz(courseTimezone);
    const nextStart = nextStartParsed.tz(courseTimezone);

    if (!currentStart.isValid() || !nextStart.isValid()) {
      continue;
    }

    if (currentStart.isAfter(nextStart)) {
      return {
        isValid: false,
        error: 'courseForm.validation.datesMustBeInOrder'
      };
    }
  }

  for (let i = 0; i < courseDates.length - 1; i++) {
    const currentEndParsed = parseDateTimeString(
      courseDates[i].endDate,
      courseDates[i].endTime,
      dayjsDateFormat,
      dayjsTimeFormat
    );
    const nextStartParsed = parseDateTimeString(
      courseDates[i + 1].startDate,
      courseDates[i + 1].startTime,
      dayjsDateFormat,
      dayjsTimeFormat
    );

    if (!currentEndParsed || !nextStartParsed) {
      continue;
    }

    const currentEnd = currentEndParsed.tz(courseTimezone);
    const nextStart = nextStartParsed.tz(courseTimezone);

    if (!currentEnd.isValid() || !nextStart.isValid()) {
      continue;
    }

    if (currentEnd.isAfter(nextStart)) {
      return {
        isValid: false,
        error: 'courseForm.validation.datesCannotOverlap'
      };
    }
  }

  return { isValid: true };
};

export const validateAllCourseDates = (
  dates: CourseDateEntry[],
  timezone: string,
  dateFormat: DateFormat,
  timeFormat: TimeFormat
): Record<string, CourseDateError> => {
  const errors: Record<string, CourseDateError> = {};

  dates.forEach((date, index) => {
    const validation = validateCourseDateEntry(date, index, dateFormat, timeFormat);
    if (!validation.isValid && validation.error) {
      errors[date.id] = validation.error;
    }
  });

  const orderingValidation = validateCourseDatesOrdering(dates, timezone, dateFormat, timeFormat);

  if (!orderingValidation.isValid) {
    const orderingErrorKey =
      orderingValidation.error ?? 'courseForm.validation.sessionInvalidOrdering';
    dates.forEach(date => {
      if (!errors[date.id]) {
        errors[date.id] = orderingErrorKey;
      }
    });
  }

  return errors;
};

export const validateCourseDatesFormData = (formData: {
  expectedParticipants?: number;
  maxParticipants?: number;
}): Record<string, string> => {
  const validationErrors: Record<string, string> = {};

  if (formData.maxParticipants === undefined || formData.maxParticipants === null) {
    validationErrors.maxParticipants = 'courseForm.validation.maxParticipantsRequired';
  } else if (formData.maxParticipants > 1000) {
    validationErrors.maxParticipants = 'courseForm.validation.maxParticipantsTooHigh';
  }

  if (
    formData.expectedParticipants !== undefined &&
    formData.expectedParticipants !== null &&
    formData.maxParticipants !== undefined &&
    formData.maxParticipants !== null &&
    formData.expectedParticipants > formData.maxParticipants
  ) {
    validationErrors.expectedParticipants = 'courseForm.validation.expectedParticipantsExceedsMax';
  }

  return validationErrors;
};
